import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { ArrowLeft, CircleNotch, Star, Lightning } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const W9_FACTORS = [
  { key: 'f1', label: 'Frequency', desc: 'Overall diff frequency', raw: 'f1_freq', score: 'f1_score', weight: 13 },
  { key: 'f2', label: 'Apr Freq', desc: 'April-specific frequency', raw: 'f2_apr_freq', score: 'f2_score', weight: 13 },
  { key: 'f3', label: 'Apr Absent', desc: 'Aprils absent (of last 4)', raw: 'f3_apr_absent', score: 'f3_score', weight: 18 },
  { key: 'f4', label: 'Gap', desc: 'Draws since last seen', raw: 'f4_gap', score: 'f4_score', weight: 18 },
  { key: 'f5', label: 'Hot/Cold', desc: 'Last 20 vs Last 100 trend', raw: 'f5_hot', score: 'f5_score', weight: 13 },
  { key: 'f6', label: 'JFM26', desc: 'Jan-Mar 2026 momentum', raw: 'f6_jfm', score: 'f6_score', weight: 13 },
  { key: 'f7', label: 'Spread (W36)', desc: 'Spread compatibility with predicted other LS', raw: 'f7_spread_raw', score: 'f7_spread_score', weight: 12 },
];

const W10_FACTORS = [
  { key: 'f1', label: 'Frequency', desc: 'Overall appearance count', raw: 'f1_freq', score: 'f1_score', weight: 9 },
  { key: 'f2', label: 'Apr Freq', desc: 'April-specific count', raw: 'f2_apr_freq', score: 'f2_score', weight: 13 },
  { key: 'f3', label: 'Apr Absent', desc: 'Aprils absent (of last 4)', raw: 'f3_apr_absent', score: 'f3_score', weight: 18 },
  { key: 'f4', label: 'Gap', desc: 'Draws since last appeared', raw: 'f4_gap', score: 'f4_score', weight: 13 },
  { key: 'f5', label: 'Hot/Cold', desc: 'Last 20 vs Last 100 trend', raw: 'f5_hot', score: 'f5_score', weight: 13 },
  { key: 'f6', label: 'JFM26', desc: 'Jan-Mar 2026 momentum', raw: 'f6_jfm', score: 'f6_score', weight: 9 },
  { key: 'f7', label: 'Remaining', desc: 'Expected remaining in 2026', raw: 'f7_remaining', score: 'f7_score', weight: 13 },
  { key: 'f8', label: 'Spread (W36)', desc: 'Spread compatibility with predicted other LS', raw: 'f8_spread_raw', score: 'f8_spread_score', weight: 12 },
];

const W39_FACTORS = [
  { key: 'gr', label: 'Gap Ratio', desc: 'Current gap / avg gap (>1 = overdue)', raw: 'gap_ratio', score: 'gap_ratio_score', weight: 40 },
  { key: 'od', label: 'Overdue', desc: 'Overdue score 0-100', raw: 'overdue_score', score: 'overdue_norm', weight: 35 },
  { key: 'cg', label: 'Current Gap', desc: 'Draws since last seen', raw: 'current_gap', score: 'current_gap_score', weight: 25 },
];

const W40_FACTORS = [
  { key: 'tp', label: 'Transition %', desc: 'P(next=X | current)', raw: 'transition_pct', score: 'transition_score', weight: 60 },
  { key: 'dv', label: 'Deviation', desc: 'Above/below expected 8.3%', raw: 'deviation', score: 'deviation_score', weight: 40 },
];

const W41_FACTORS = [
  { key: 'l10', label: 'Last 10', desc: 'Rate diff vs overall in last 10', raw: 'last_10_diff', score: 'last_10_score', weight: 45 },
  { key: 'l20', label: 'Last 20', desc: 'Rate diff vs overall in last 20', raw: 'last_20_diff', score: 'last_20_score', weight: 30 },
  { key: 'l50', label: 'Last 50', desc: 'Rate diff vs overall in last 50', raw: 'last_50_diff', score: 'last_50_score', weight: 25 },
];

const W42_FACTORS = [
  { key: 'sm', label: 'Sum Prob', desc: 'Probability of LS1+LS2 sum', raw: 'sum_pct', score: 'sum_score', weight: 50 },
  { key: 'sp', label: 'Spread Prob', desc: 'Probability of LS2-LS1 spread', raw: 'spread_pct', score: 'spread_score', weight: 50 },
];

const ALL_WINDOW_FACTORS = {
  w9: { label: 'W34: Difference', color: 'blue', factors: W9_FACTORS },
  w10: { label: 'W35: Appearance', color: 'purple', factors: W10_FACTORS },
  w39: { label: 'W37: Gap/Overdue', color: 'rose', factors: W39_FACTORS },
  w40: { label: 'W38: Markov', color: 'emerald', factors: W40_FACTORS },
  w41: { label: 'W39: Hot/Cold', color: 'orange', factors: W41_FACTORS },
  w42: { label: 'W40: Sum/Spread', color: 'teal', factors: W42_FACTORS },
};

function ScoreBar({ score, max = 10 }) {
  const pct = Math.min((score / max) * 100, 100);
  const color = pct >= 70 ? 'bg-red-500' : pct >= 40 ? 'bg-amber-400' : 'bg-blue-400';
  return (
    <div className="w-full bg-gray-200 rounded-full h-2.5 relative">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function ConsensusTable({ consensus, ballData }) {
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="space-y-2" data-testid="ls-consensus-table">
      {/* Header */}
      <div className="grid grid-cols-[36px_40px_52px_52px_52px_52px_52px_52px_70px_1fr] gap-1 px-2 text-[9px] font-bold text-gray-500 uppercase">
        <span>Rk</span>
        <span>#</span>
        <span className="text-blue-600">W34</span>
        <span className="text-purple-600">W35</span>
        <span className="text-rose-600">W37</span>
        <span className="text-emerald-600">W38</span>
        <span className="text-orange-600">W39</span>
        <span className="text-teal-600">W40</span>
        <span>Total</span>
        <span></span>
      </div>

      {consensus.map((c, i) => (
        <div key={c.number}>
          <div
            className={`grid grid-cols-[36px_40px_52px_52px_52px_52px_52px_52px_70px_1fr] gap-1 items-center px-2 py-2 rounded-lg cursor-pointer transition-colors ${i === 0 ? 'bg-red-50 border-2 border-red-300' : i < 3 ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-200'} hover:bg-blue-50`}
            onClick={() => setExpanded(expanded === c.number ? null : c.number)}
            data-testid={`ls-row-${c.number}`}
          >
            <span className={`font-bold text-sm ${i < 3 ? 'text-red-700' : 'text-gray-500'}`}>{i + 1}</span>
            <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-mono font-bold text-sm text-white ${i === 0 ? 'bg-red-600' : i < 3 ? 'bg-amber-500' : 'bg-gray-400'}`}>{c.number}</span>
            <span className="text-[11px] font-bold text-blue-700">{c.w9_score.toFixed(1)}</span>
            <span className="text-[11px] font-bold text-purple-700">{c.w10_score.toFixed(1)}</span>
            <span className="text-[11px] font-bold text-rose-700">{c.w39_score.toFixed(1)}</span>
            <span className="text-[11px] font-bold text-emerald-700">{c.w40_score.toFixed(1)}</span>
            <span className="text-[11px] font-bold text-orange-700">{c.w41_score.toFixed(1)}</span>
            <span className="text-[11px] font-bold text-teal-700">{c.w42_score.toFixed(1)}</span>
            <span className={`text-sm font-black ${i === 0 ? 'text-red-700' : i < 3 ? 'text-amber-700' : 'text-gray-700'}`}>{c.combined_score.toFixed(2)}</span>
            <div className="flex-1">
              <div className="w-full bg-gray-200 rounded-full h-3 relative overflow-hidden">
                <div className={`h-full rounded-full ${i === 0 ? 'bg-red-500' : i < 3 ? 'bg-amber-400' : 'bg-blue-400'}`} style={{ width: `${(c.combined_score / (consensus[0]?.combined_score || 1)) * 100}%` }} />
              </div>
            </div>
          </div>

          {/* Expanded factor breakdown — all 6 windows */}
          {expanded === c.number && (
            <div className="ml-2 mr-2 mt-1 mb-2 rounded-lg border border-blue-200 bg-blue-50/50 p-3">
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                {Object.entries(ALL_WINDOW_FACTORS).map(([wKey, wDef]) => {
                  const factors_data = c[`${wKey}_factors`] || {};
                  const window_score = c[`${wKey}_score`] || 0;
                  const colorMap = { blue: 'blue', purple: 'purple', rose: 'rose', emerald: 'emerald', orange: 'orange', teal: 'teal' };
                  const col = colorMap[wDef.color] || 'gray';
                  return (
                    <div key={wKey} className={`border border-${col}-200 rounded-lg p-2 bg-white`}>
                      <h4 className={`text-[10px] font-bold text-${col}-800 mb-1.5`}>
                        {wDef.label}
                        {wKey === 'w9' && c.w9_diff != null && <span className="text-gray-400 ml-1">(diff {c.w9_diff > 0 ? '+' : ''}{c.w9_diff})</span>}
                        {wKey === 'w41' && factors_data.status && <span className={`ml-1 px-1 py-0.5 rounded text-[8px] font-black ${factors_data.status === 'HOT' ? 'bg-red-100 text-red-700' : factors_data.status === 'COLD' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{factors_data.status}</span>}
                      </h4>
                      <table className="w-full text-[9px]">
                        <thead><tr className="text-gray-400"><th className="text-left px-0.5">Factor</th><th className="text-center px-0.5">Wt</th><th className="text-center px-0.5">Raw</th><th className="text-center px-0.5">Sc</th><th className="w-12"></th></tr></thead>
                        <tbody>
                          {wDef.factors.map(f => {
                            const raw = factors_data[f.raw];
                            const score = factors_data[f.score] || 0;
                            return (
                              <tr key={f.key} className="border-t border-gray-100">
                                <td className="px-0.5 py-0.5 font-bold text-gray-600" title={f.desc}>{f.label}</td>
                                <td className="text-center px-0.5 text-gray-400">{f.weight}%</td>
                                <td className="text-center px-0.5 font-mono">{typeof raw === 'number' ? (Number.isInteger(raw) ? raw : raw.toFixed(2)) : '-'}</td>
                                <td className={`text-center px-0.5 font-bold text-${col}-700`}>{score.toFixed(1)}</td>
                                <td className="px-0.5"><ScoreBar score={score} /></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div className={`text-right text-[10px] font-black text-${col}-800 mt-1 border-t border-${col}-200 pt-0.5`}>Total: {window_score.toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function LSPrediction() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try { const res = await axios.get(`${API}/analysis/ls-prediction`); setData(res.data); }
    catch { if (!silent) toast.error('Failed to load LS prediction'); }
    finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDrawsUpdated(fetchData);
  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100" data-testid="ls-prediction-page">
      <Toaster position="top-right" />
      <header className="bg-gradient-to-r from-[#002FA7] to-[#0040DD] px-6 py-4 flex items-center gap-4 shadow-lg">
        <button onClick={() => navigate('/')} className="text-white/70 hover:text-white" data-testid="back-btn"><ArrowLeft size={22} /></button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>W33: LS Prediction: LS1 & LS2</h1>
          <p className="text-xs text-blue-200 mt-0.5">6-Window scoring (W43 spread embedded in W9/W10) — Two-pass cross-dependency</p>
        </div>
        {data && (
          <div className="flex items-center gap-3">
            <div className="bg-white/10 rounded-lg px-3 py-1.5 text-white text-xs">
              Last: LS1=<span className="font-bold text-amber-300">{data.last_draw.ls1}</span> LS2=<span className="font-bold text-amber-300">{data.last_draw.ls2}</span>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-[1600px] mx-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20"><CircleNotch size={40} className="animate-spin text-blue-600" /></div>
        ) : data ? (
          <Tabs defaultValue="lucky_star_1">
            <TabsList className="mb-4">
              <TabsTrigger value="lucky_star_1" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-ls1">
                LS1 (Ball 6) — Current: {data.balls.lucky_star_1?.last_value}
              </TabsTrigger>
              <TabsTrigger value="lucky_star_2" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-ls2">
                LS2 (Ball 7) — Current: {data.balls.lucky_star_2?.last_value}
              </TabsTrigger>
            </TabsList>

            {['lucky_star_1', 'lucky_star_2'].map(bk => {
              const bd = data.balls[bk];
              if (!bd) return null;
              return (
                <TabsContent key={bk} value={bk}>
                  {/* Weight summaries */}
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                    {Object.entries(ALL_WINDOW_FACTORS).map(([wKey, wDef]) => (
                      <div key={wKey} className="bg-white border border-gray-200 rounded-lg p-2">
                        <h3 className={`text-[10px] font-bold text-${wDef.color}-800 mb-1`}>{wDef.label} ({(bd.window_weights[wKey] * 100)}%)</h3>
                        <div className="flex gap-1 flex-wrap">
                          {wDef.factors.map(f => (
                            <span key={f.key} className="bg-gray-100 text-gray-700 text-[8px] font-bold px-1.5 py-0.5 rounded">{f.label}: {f.weight}%</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Cross-window weights */}
                  <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-gray-600">Window Weights:</span>
                    <span className="bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">W34: {bd.window_weights.w9 * 100}%</span>
                    <span className="bg-purple-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">W35: {bd.window_weights.w10 * 100}%</span>
                    <span className="bg-rose-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">W37: {bd.window_weights.w39 * 100}%</span>
                    <span className="bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">W38: {bd.window_weights.w40 * 100}%</span>
                    <span className="bg-orange-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">W39: {bd.window_weights.w41 * 100}%</span>
                    <span className="bg-teal-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">W40: {bd.window_weights.w42 * 100}%</span>
                    <span className="text-[10px] text-gray-400 ml-auto">W36 spread embedded in W34/W35 | Predicted other: {bd.predicted_other}</span>
                  </div>

                  {/* Consensus Table */}
                  <ConsensusTable consensus={bd.consensus} ballData={bd} />
                </TabsContent>
              );
            })}
          </Tabs>
        ) : null}
      </main>
    </div>
  );
}
